import { z } from 'zod';

export const AgentPromptStatusSchema = z.enum(['draft', 'active', 'archived']);
export type AgentPromptStatus = z.infer<typeof AgentPromptStatusSchema>;

export const AgentGlossaryKindSchema = z.enum(['glossary', 'metric']);
export type AgentGlossaryKind = z.infer<typeof AgentGlossaryKindSchema>;

export const AgentGlossaryStatusSchema = z.enum([
  'draft',
  'proposed_by_agent',
  'edited_by_user',
  'approved',
  'archived',
  'rejected',
]);
export type AgentGlossaryStatus = z.infer<typeof AgentGlossaryStatusSchema>;

export const AgentMemoryStatusSchema = z.enum([
  'proposed_by_agent',
  'edited_by_user',
  'approved',
  'rejected',
  'archived',
]);
export type AgentMemoryStatus = z.infer<typeof AgentMemoryStatusSchema>;

export const AgentPromptVersionSchema = z
  .object({
    id: z.string().uuid(),
    content: z.string().max(20_000),
    status: AgentPromptStatusSchema,
    createdByUserId: z.string().uuid().nullable(),
    activatedByUserId: z.string().uuid().nullable(),
    createdAt: z.string().datetime({ offset: true }),
    activatedAt: z.string().datetime({ offset: true }).nullable(),
    archivedAt: z.string().datetime({ offset: true }).nullable(),
  })
  .strict();
export type AgentPromptVersion = z.infer<typeof AgentPromptVersionSchema>;

export const AgentGlossaryTermSchema = z
  .object({
    id: z.string().uuid(),
    kind: AgentGlossaryKindSchema,
    term: z.string().max(200),
    definition: z.string().max(4000),
    status: AgentGlossaryStatusSchema,
    createdByUserId: z.string().uuid().nullable(),
    updatedByUserId: z.string().uuid().nullable(),
    approvedByUserId: z.string().uuid().nullable(),
    createdAt: z.string().datetime({ offset: true }),
    updatedAt: z.string().datetime({ offset: true }),
    approvedAt: z.string().datetime({ offset: true }).nullable(),
    archivedAt: z.string().datetime({ offset: true }).nullable(),
    rejectedAt: z.string().datetime({ offset: true }).nullable(),
  })
  .strict();
export type AgentGlossaryTerm = z.infer<typeof AgentGlossaryTermSchema>;

export const AgentConfigSummarySchema = z
  .object({
    activePromptVersionId: z.string().uuid().nullable(),
    activePromptActivatedAt: z.string().datetime({ offset: true }).nullable(),
    approvedGlossaryCount: z.number().int().nonnegative(),
    approvedMetricCount: z.number().int().nonnegative(),
    glossaryVersion: z.string(),
    memoryEnabled: z.boolean(),
    catalogVersion: z.string(),
    dataPolicy: z.string().max(1000),
  })
  .strict();
export type AgentConfigSummary = z.infer<typeof AgentConfigSummarySchema>;

export const AgentMemoryEntrySchema = z
  .object({
    id: z.string().uuid(),
    title: z.string().max(200),
    summary: z.string().max(4000),
    status: AgentMemoryStatusSchema,
    createdByUserId: z.string().uuid().nullable(),
    updatedByUserId: z.string().uuid().nullable(),
    approvedByUserId: z.string().uuid().nullable(),
    createdAt: z.string().datetime({ offset: true }),
    updatedAt: z.string().datetime({ offset: true }),
    approvedAt: z.string().datetime({ offset: true }).nullable(),
    archivedAt: z.string().datetime({ offset: true }).nullable(),
    rejectedAt: z.string().datetime({ offset: true }).nullable(),
  })
  .strict();
export type AgentMemoryEntry = z.infer<typeof AgentMemoryEntrySchema>;

export const CreateAgentPromptDraftInputSchema = z
  .object({ content: z.string().trim().min(1).max(20_000) })
  .strict();
export type CreateAgentPromptDraftInput = z.infer<typeof CreateAgentPromptDraftInputSchema>;

export const CreateAgentGlossaryTermInputSchema = z
  .object({
    kind: AgentGlossaryKindSchema.default('glossary'),
    term: z.string().trim().min(1).max(200),
    definition: z.string().trim().min(1).max(4000),
  })
  .strict();
export type CreateAgentGlossaryTermInput = z.infer<typeof CreateAgentGlossaryTermInputSchema>;

export const PatchAgentGlossaryTermInputSchema = z
  .object({
    kind: AgentGlossaryKindSchema.optional(),
    term: z.string().trim().min(1).max(200).optional(),
    definition: z.string().trim().min(1).max(4000).optional(),
    status: AgentGlossaryStatusSchema.optional(),
  })
  .strict()
  .refine((value) => Object.keys(value).length > 0, { message: 'at least one field is required' });
export type PatchAgentGlossaryTermInput = z.infer<typeof PatchAgentGlossaryTermInputSchema>;

export const CreateAgentMemoryEntryInputSchema = z
  .object({
    title: z.string().trim().min(1).max(200),
    summary: z.string().trim().min(1).max(4000),
  })
  .strict();
export type CreateAgentMemoryEntryInput = z.infer<typeof CreateAgentMemoryEntryInputSchema>;

export const PatchAgentMemoryEntryInputSchema = z
  .object({
    title: z.string().trim().min(1).max(200).optional(),
    summary: z.string().trim().min(1).max(4000).optional(),
    status: AgentMemoryStatusSchema.optional(),
  })
  .strict()
  .refine((value) => Object.keys(value).length > 0, { message: 'at least one field is required' });
export type PatchAgentMemoryEntryInput = z.infer<typeof PatchAgentMemoryEntryInputSchema>;

export const AgentPromptVersionsResponseSchema = z
  .object({ items: z.array(AgentPromptVersionSchema) })
  .strict();
export type AgentPromptVersionsResponse = z.infer<typeof AgentPromptVersionsResponseSchema>;

export const AgentGlossaryTermsResponseSchema = z
  .object({ items: z.array(AgentGlossaryTermSchema) })
  .strict();
export type AgentGlossaryTermsResponse = z.infer<typeof AgentGlossaryTermsResponseSchema>;

export const AgentMemoryEntriesResponseSchema = z
  .object({ items: z.array(AgentMemoryEntrySchema) })
  .strict();
export type AgentMemoryEntriesResponse = z.infer<typeof AgentMemoryEntriesResponseSchema>;
